import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { Switch } from '@headlessui/react';
import { useJobCategories } from '../../context/JobCategoryContext';
import { useRoutePath } from '../../utils/getRoutePath';
import { useLoading } from '../../context/LoadingContext';
  
const schema = yup.object().shape({
  name: yup.string().required('Category name is required'),
  parent_category_id: yup.string().nullable(),
  is_automatically_pre_load: yup.boolean().default(false),
  default_job_priority_id: yup.string().test(
    'required-when-auto-preload',
    'Default job priority is required when auto pre-load is enabled',
    function (value) {
      const { is_automatically_pre_load } = this.parent;
      if (is_automatically_pre_load) {
        return !!value;
      }
      return true;
    }
  ),

  default_duration: yup.string().test(
    'required-when-auto-preload',
    'Default duration is required when auto pre-load is enabled',
    function (value) {
      const { is_automatically_pre_load } = this.parent;
      if (is_automatically_pre_load) {
        return !!value;
      }
      return true;
    }
  ),
  
  default_job_description: yup.string().nullable(),
  default_notes_for_techs: yup.string().nullable(),
  default_custom_field_ids: yup.array().nullable(),
  active: yup.boolean().default(true)
});

const JobCategoryForm = () => {
  const location = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { addJobCategory, updateJobCategory, getJobCategoryById, getJobCategories,getJobCategoryAdditionalData } = useJobCategories();
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [isAutoPreLoad, setIsAutoPreLoad] = useState(false);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/job-categories');
  const ranOnceRef = useRef(false);
  const ranOnceRef2 = useRef(false);
  const { showLoading, hideLoading } = useLoading();
  const [priorities,setPriorities] = useState([]);
  const [categoryCustomFields,setCategoryCustomFields] = useState([]);
 
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    setError,
    watch
  } = useForm({
    resolver: yupResolver(schema)
  });
 

  useEffect(() => {
    if(!id){
      reset({
        name:'',
        parent_category_id:'',
        is_automatically_pre_load: false,
        default_job_priority_id:'',
        default_duration:'',
        default_job_description:'',
        default_notes_for_techs:'',
        default_custom_field_ids: [],
        active:true
      }); 
      setIsAutoPreLoad(false);
    }
  }, [id,reset]);

  useEffect(() => {
    const loadData = async () => {
     
      const response = await getJobCategories(1, { all: true ,active:true});
      setCategories(response?.data);
    }
    if(ranOnceRef && !ranOnceRef.current){
      loadData();
      ranOnceRef.current = true
    }
  }, [getJobCategories]);

  useEffect(() => {
    const fetchAdditionalData = async () => {
      const response = await getJobCategoryAdditionalData();
      
      setPriorities(response?.priorities);
      setCategoryCustomFields(response?.categoryCustomFields);
    }
    fetchAdditionalData();
  }, [getJobCategoryAdditionalData]);

  // Load categories data
  useEffect(() => {
    const loadData = async () => {
      try {
        showLoading()
        // Load all categories for parent dropdown
         
        if (id) {
         
          const categoryResponse = await getJobCategoryById(id);
          setIsLoading(true)
        
          if (categoryResponse.status === 200) {
            let  category = categoryResponse.data;
            
            const formData = {
              name: category.name,
              parent_category_id: category.parent_category_id || '',
              is_automatically_pre_load: category.is_automatically_pre_load || false,
              default_job_priority_id: category.default_job_priority_id || '',
              default_duration: category.default_duration || '',
              default_job_description: category.default_job_description || '',
              default_notes_for_techs: category.default_notes_for_techs || '',
              default_custom_field_ids: category.default_custom_field_ids ? JSON.parse(category.default_custom_field_ids) : [],
              active: category.active
            };
            setIsActive(category.active);
            setIsAutoPreLoad(category.is_automatically_pre_load || false);
            reset(formData);
          } else {
            toast.error('Category not found');
            navigate(getRoutePath('/job-categories'));
          }
          hideLoading()
          setIsLoading(false)
        }
      } catch (error) {
        setIsLoading(false)
        hideLoading()
        console.error('Error loading data:', error);
        toast.error('Error loading category data');
      } finally {
        setIsLoading(false)
        hideLoading()
        
      }
    };
    if (ranOnceRef2 && !ranOnceRef2.current) {
      loadData();
      ranOnceRef2.current = true;
    }
  }, [id, reset, navigate, getJobCategoryById, getRoutePath, initialized,getJobCategories,hideLoading,showLoading,ranOnceRef2]);

  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading()
    try {
      const formData = {
        ...data,
        active: isActive,
        is_automatically_pre_load: isAutoPreLoad,
        default_custom_field_ids: data.default_custom_field_ids ? JSON.stringify(data.default_custom_field_ids) : null
      };
      
      let response;
      if (id) {
        // Update existing category
        response = await updateJobCategory(id, formData);
      } else {
        // Create new category
        response = await addJobCategory(formData);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Category updated successfully!' : 'Category created successfully!');
        navigate(getRoutePath('/job-categories'));
      } else if (response.status === 422) {
        // Handle validation errors
        Object.keys(response.errors).forEach(field => {
          setError(field, { message: response.errors[field][0] });
          toast.error(response.errors[field][0]);
        });
      } else {
        toast.error('Error saving category');
      }
      hideLoading()
    } catch (error) {
      toast.error('Error saving category');
      console.error('Error:', error);
      hideLoading()
    } finally {
      hideLoading()
      setIsLoading(false);
    }
  };

  const handleCustomFieldChange = (fieldId, checked) => {
    const currentFields = watch('default_custom_field_ids') || [];
    let updatedFieldIds;
    
    if (checked) {
        updatedFieldIds = [...currentFields, fieldId];
    } else {
        updatedFieldIds = currentFields.filter(field => field !== fieldId);
    }
    
    setValue('default_custom_field_ids', updatedFieldIds);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'Edit Job Category' : 'Create New Job Category'}
      </h1>
      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700">Category Name</label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter category name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Parent Category</label>
            <select
              {...register('parent_category_id')}
              value={watch('parent_category_id') || ''}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.parent_category_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            >
              <option value="">Select a parent category</option>
              {categories?.map((category) => (
                <option key={category.id} value={category.id} >
                  {category.name}
                </option>
              ))}
            </select>
            {errors.parent_category_id && (
              <p className="mt-1 text-sm text-red-600">{errors.parent_category_id.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Automatically Pre-Load Category Defaults</label>
            <Switch
              checked={isAutoPreLoad}
              onChange={(value) => {
                setIsAutoPreLoad(value);
                setValue('is_automatically_pre_load', value);
              }}
              className={`${
                isAutoPreLoad ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  isAutoPreLoad ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>

          {isAutoPreLoad && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">Default Job Priority</label>
                <select
                  {...register('default_job_priority_id')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.default_job_priority_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  value={watch('default_job_priority_id') || ''}
                >
                  <option value="">Select priority</option>
                  {priorities.map((priority) => (
                    <option key={priority.id} value={priority.id}>
                      {priority.name}
                    </option>
                  ))}
                </select>
                {errors.default_job_priority_id && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_job_priority_id.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Duration (hh:mm)</label>
                <input
                  type="time"
                  {...register('default_duration')}
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.default_duration ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                />
                {errors.default_duration && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_duration.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Job Description</label>
                <textarea
                  {...register('default_job_description')}
                  rows="3"
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.default_job_description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter default job description"
                />
                {errors.default_job_description && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_job_description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Default Notes For Techs</label>
                <textarea
                  {...register('default_notes_for_techs')}
                  rows="3"
                  className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.default_notes_for_techs ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter default notes for technicians"
                />
                {errors.default_notes_for_techs && (
                  <p className="mt-1 text-sm text-red-600">{errors.default_notes_for_techs.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Default Custom Fields For This Category</label>
                <div className="space-y-2 max-h-60 overflow-y-auto border border-gray-300 rounded-md p-4">
                  {categoryCustomFields.map((field, index) => (
                    <div key={index} className="flex items-center">
                      <input
                        type="checkbox"
                        id={`custom-field-${index}`}
                        checked={watch('default_custom_field_ids')?.includes(field.id) || false}
                        onChange={(e) => handleCustomFieldChange(field.id, e.target.checked)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <label htmlFor={`custom-field-${index}`} className="ml-2 text-sm text-gray-700">
                        {field.name}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Activation Status</label>
            <Switch
              checked={isActive}
              onChange={(value) => {
                setIsActive(value);
                setValue('active', value);
              }}
              className={`${
                isActive ? 'bg-[#009efb]' : 'bg-gray-200'
              } relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#009efb] focus:ring-offset-2`}
            >
              <span
                className={`${
                  isActive ? 'translate-x-6' : 'translate-x-1'
                } inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
              />
            </Switch>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              {isLoading ? 'Saving...' : (id ? 'Update Category' : 'Create Category')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default JobCategoryForm; 